// Anita Week 8 / Day 2 — Plants: Parts and Life Cycle.
// Verbatim from FINAL Week 8 Part A source-of-truth package. No invention.
// SAFETY: drawn / household plant only; parentGuide explicitly forbids eating
// plants, seeds, berries, mushrooms, or picking wild plants without parent.

const day2 = {
  id: 'day-2',
  studentId: 'anita',
  weekId: 'week-8',
  dayId: 'day-2',
  status: 'active',
  title: 'Растения: части растения и жизненный цикл / Plants: Parts and Life Cycle',
  shortTitle: 'Растения и цикл / Plants and Cycle',
  theme:
    'Анита учится видеть части растения, понимать простой жизненный цикл и записывать данные роста. / Anita learns plant parts, a simple life cycle, and how to record growth data.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'science', 'plants', 'life-cycle', 'data', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни нашла маленькое семя и нарисовала путь растения: семя, росток, стебель, листья, цветок. Сегодня Анита узнает, из каких частей состоит растение и как оно меняется со временем. / Nimny finds a small seed and draws a plant path: seed, sprout, stem, leaves, flower. Today Anita learns plant parts and how a plant changes over time.',
  goals: [
    'Узнать части растения. / Learn plant parts.',
    'Понять простой жизненный цикл растения. / Understand a simple plant life cycle.',
    'Читать таблицу роста. / Read a growth table.',
    'Решать задачи с умножением, сложением и графиком. / Solve multiplication, addition and graph tasks.',
    'Сделать карточку жизненного цикла. / Make a life cycle card.',
    'Объяснить родителю, что нужно растению. / Explain what a plant needs.'
  ],
  vocabulary: [
    { term: 'растение / plant', definition: 'живой организм, который обычно растёт из семени. / a living organism that usually grows from a seed.' },
    { term: 'семя / seed', definition: 'маленькое начало растения. / a small start of a plant.' },
    { term: 'корень / root', definition: 'часть растения в земле, помогает брать воду. / the part in the soil that takes water.' },
    { term: 'стебель / stem', definition: 'держит растение и несёт воду. / holds the plant and carries water.' },
    { term: 'лист / leaf', definition: 'часть растения, которая помогает получать свет. / the part that captures light.' },
    { term: 'цветок / flower', definition: 'часть растения, из которой могут появиться семена. / the part where seeds can appear.' },
    { term: 'росток / sprout', definition: 'маленькое растение, которое только начало расти. / a tiny plant that just started growing.' },
    { term: 'цикл / cycle', definition: 'путь, который повторяется. / a path that repeats.' },
    { term: 'вода / water', definition: 'нужна растению для жизни. / needed by a plant.' },
    { term: 'свет / light', definition: 'помогает растению расти. / helps a plant grow.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Задание 1 — Слова растения / Plant Words',
      prompt: 'Прочитай по-русски:\nрастение\nсемя\nкорень\nстебель\nлист\nцветок\nросток\nцикл\nвода\nсвет',
      ruPrimaryText: 'Прочитай 10 русских слов растения.',
      enHelpText: 'Read the Russian plant words first. English is only for help.',
      answer: 'Анита пробует читать русские слова. / Anita attempts the Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Задание 2 — Маленькое чтение / Short Reading',
      prompt: 'Прочитай по-русски:\nСемя может стать ростком.\nКорень берёт воду из земли.\nЛистья помогают растению получать свет.\nЖизненный цикл показывает изменения.',
      ruPrimaryText: 'Прочитай 4 короткие фразы про растение и цикл.',
      enHelpText: 'A seed can become a sprout. A root takes water from soil. Leaves help a plant get light. A life cycle shows changes.',
      answer: 'Анита пробует читать 4 фразы. / Anita attempts the short reading.'
    },
    {
      id: 'plant-parts-match',
      type: 'text',
      title: 'Задание 3 — Части растения / Plant Parts',
      prompt:
        'Соедини часть растения и работу.\n\n1. корень\n2. стебель\n3. лист\n4. цветок\n5. семя\n\nA. помогает получать свет\nB. держит растение\nC. из него может начаться новое растение\nD. берёт воду из земли\nE. может дать семена',
      ruPrimaryText: 'Соедини 5 частей растения с их работой.',
      enHelpText: 'Match each plant part with its job.',
      answer: '1-D, 2-B, 3-A, 4-E, 5-C.'
    },
    {
      id: 'life-cycle-order',
      type: 'text',
      title: 'Задание 4 — Жизненный цикл / Life Cycle',
      prompt: 'Поставь шаги в правильном порядке.\n\nA. росток\nB. семя\nC. взрослое растение\nD. цветок\nE. новое семя',
      ruPrimaryText: 'Поставь 5 шагов жизненного цикла растения по порядку.',
      enHelpText: 'Order the 5 life-cycle steps.',
      answer: 'B → A → C → D → E.'
    },
    {
      id: 'math-plant-growth-table',
      type: 'text',
      title: 'Задание 5 — Таблица роста / Growth Table',
      prompt:
        'Нимни записала рост растения:\n\nДень 1 — 2 см\nДень 3 — 5 см\nДень 5 — 9 см\nДень 7 — 14 см\n\nОтветь:\n1. На сколько см растение выросло с Дня 1 до Дня 3?\n2. На сколько см растение выросло с Дня 3 до Дня 5?\n3. На сколько см растение выросло с Дня 5 до Дня 7?\n4. На сколько см растение выросло с Дня 1 до Дня 7?',
      ruPrimaryText: 'Прочитай таблицу роста и реши 4 задачи на вычитание.',
      enHelpText: 'Growth table: Day 1=2, Day 3=5, Day 5=9, Day 7=14. Answer 4 questions.',
      answer: '1) 5 − 2 = 3 см. 2) 9 − 5 = 4 см. 3) 14 − 9 = 5 см. 4) 14 − 2 = 12 см.'
    },
    {
      id: 'math-seed-arrays',
      type: 'text',
      title: 'Задание 6 — Семена в рядах / Seed Arrays',
      prompt:
        'Анита раскладывает семена.\n\nВ одном лотке 4 ряда.\nВ каждом ряду по 6 семян.\nВо втором лотке 3 ряда.\nВ каждом ряду по 5 семян.\n\nОтветь:\n1. Сколько семян в первом лотке?\n2. Сколько семян во втором лотке?\n3. Сколько семян всего?\n4. Если 39 семян разложить поровну в 3 стаканчика, сколько семян в каждом?',
      ruPrimaryText: 'Реши 4 задачи на умножение, сложение и деление с семенами.',
      enHelpText: 'Tray 1: 4 rows × 6 seeds. Tray 2: 3 rows × 5 seeds. 39 ÷ 3. Answer 4 questions.',
      answer: '1) 4 × 6 = 24. 2) 3 × 5 = 15. 3) 24 + 15 = 39. 4) 39 ÷ 3 = 13.'
    },
    {
      id: 'data-plant-bar-graph',
      type: 'text',
      title: 'Задание 7 — График роста / Growth Graph',
      prompt:
        'Сделай мини-график высоты растения.\n\nДанные:\nДень 1 — 2 см\nДень 3 — 5 см\nДень 5 — 9 см\nДень 7 — 14 см\n\nНарисуй полоски или квадратики в тетради.\n\nОтветь:\n1. В какой день растение было самым высоким?\n2. В какой день растение было ниже 10 см?\n3. Сколько дней в таблице?',
      ruPrimaryText: 'Прочитай данные роста и ответь на 3 вопроса.',
      enHelpText: 'Draw a mini graph from the growth data and answer 3 questions.',
      answer: '1) День 7. 2) День 1, День 3, День 5. 3) 4 дня.'
    },
    {
      id: 'science-plant-needs',
      type: 'text',
      title: 'Задание 8 — Что нужно растению? / What Does a Plant Need?',
      prompt:
        'Выбери, что помогает растению жить и расти.\n\n1. вода\n2. свет\n3. воздух\n4. место для корней\n5. конфета\n6. игрушка\n7. немного тепла\n8. телефон\n\nОтветь:\nКакие пункты нужны растению?',
      ruPrimaryText: 'Выбери из 8 пунктов те, что нужны растению.',
      enHelpText: 'Pick the items a plant actually needs to live and grow.',
      answer: 'Нужны: 1, 2, 3, 4, 7. Не нужны: 5, 6, 8.'
    },
    {
      id: 'spelling-plant',
      type: 'text',
      title: 'Задание 9 — Правописание / Spelling',
      prompt: 'Напиши:\nсемя\nкорень\nлист\nцветок\nсвет\n\nВставь буквы:\nс_мя\nк_рень\nл_ст\nцвет_к\nсв_т',
      ruPrimaryText: 'Напиши 5 слов растения и вставь пропущенные буквы.',
      enHelpText: 'Write 5 plant words and fill in the missing letters.',
      answer: 'семя, корень, лист, цветок, свет.'
    },
    {
      id: 'creative-plant-cycle-card',
      type: 'text',
      title: 'Задание 10 — Карточка жизненного цикла / Life Cycle Card',
      prompt:
        'Нарисуй 5 шагов жизненного цикла растения:\n\n1. семя\n2. росток\n3. взрослое растение\n4. цветок\n5. новое семя\n\nНапиши:\nРастению нужны ______, ______ и ______.',
      ruPrimaryText: 'Нарисуй 5 шагов цикла и напиши 3 потребности растения.',
      enHelpText: 'Draw the 5-step life cycle and write the plant needs.',
      hint: 'Используй рисунок или комнатное растение — не срывай уличных растений. / Use a drawing or a houseplant; do not pick wild plants.',
      answer: 'Open-ended. Accept if Anita draws the 5-step cycle and names plant needs such as water, light, air.'
    },
    {
      id: 'speech-plant-explain',
      type: 'text',
      title: 'Задание 11 — Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\nУ растения есть корень, стебель, листья и цветок.\nРастению нужны вода, свет и воздух.\nЖизненный цикл начинается с семени.',
      ruPrimaryText: 'Объясни родителю части растения и его потребности по-русски.',
      enHelpText: 'Tell a parent in Russian: plant parts and plant needs and that the life cycle starts with a seed.',
      answer: 'Accept if Anita explains plant parts and needs in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Прочитай 4 фразы.',
    '3. Соедини части растения и работу.',
    '4. Поставь жизненный цикл по порядку.',
    '5. Реши задачи по таблице роста.',
    '6. Реши задачи про семена в рядах.',
    '7. Нарисуй график роста.',
    '8. Сделай карточку жизненного цикла.',
    '9. Объясни родителю, что нужно растению.'
  ],
  printWorksheet: {
    title: 'Растения: части растения и жизненный цикл / Plants: Parts and Life Cycle',
    subtitle: 'Неделя 8 · День 2 / Week 8 · Day 2',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Нимни изучает растение: семя, корень, стебель, листья, цветок и жизненный цикл. / Nimny studies the plant: seed, root, stem, leaves, flower and life cycle.' },
          { type: 'text', text: 'Задание 1 — Слова растения. Прочитай и обведи:' },
          { type: 'list', items: [
            'растение / plant',
            'семя / seed',
            'корень / root',
            'стебель / stem',
            'лист / leaf',
            'цветок / flower',
            'росток / sprout',
            'цикл / cycle',
            'вода / water',
            'свет / light'
          ] },
          { type: 'text', text: 'Задание 2 — Маленькое чтение. Поставь галочку:' },
          { type: 'list', items: [
            '□ Семя может стать ростком.',
            '□ Корень берёт воду из земли.',
            '□ Листья помогают растению получать свет.',
            '□ Жизненный цикл показывает изменения.'
          ] },
          { type: 'text', text: 'Задание 3 — Части растения. Подсказки: A. помогает получать свет. B. держит растение. C. из него может начаться новое растение. D. берёт воду из земли. E. может дать семена.' },
          { type: 'list', items: [
            '1. корень → ______',
            '2. стебель → ______',
            '3. лист → ______',
            '4. цветок → ______',
            '5. семя → ______'
          ] }
        ]
      },
      {
        title: 'Рост и математика / Growth and Math',
        blocks: [
          { type: 'text', text: 'Задание 4 — Жизненный цикл. Поставь по порядку: A. росток. B. семя. C. взрослое растение. D. цветок. E. новое семя.' },
          { type: 'list', items: [
            'Порядок: ____ → ____ → ____ → ____ → ____'
          ] },
          { type: 'text', text: 'Задание 5 — Таблица роста. День 1 — 2 см, День 3 — 5 см, День 5 — 9 см, День 7 — 14 см.' },
          { type: 'list', items: [
            '1. День 1 до День 3: 5 − 2 = ______',
            '2. День 3 до День 5: 9 − 5 = ______',
            '3. День 5 до День 7: 14 − 9 = ______',
            '4. День 1 до День 7: 14 − 2 = ______'
          ] },
          { type: 'text', text: 'Задание 6 — Семена в рядах (умножение и деление):' },
          { type: 'list', items: [
            'Лоток 1: 4 ряда × 6 семян = ______',
            'Лоток 2: 3 ряда × 5 семян = ______',
            'Всего семян: ______ + ______ = ______',
            '39 семян ÷ 3 стаканчика = ______'
          ] },
          { type: 'text', text: 'Задание 7 — График роста.' },
          { type: 'list', items: [
            'День 1 — 2 см: □ □',
            'День 3 — 5 см: □ □ □ □ □',
            'День 5 — 9 см: □ □ □ □ □ □ □ □ □',
            'День 7 — 14 см: □ □ □ □ □ □ □ □ □ □ □ □ □ □',
            'Самый высокий день: ______',
            'Ниже 10 см: __________________________',
            'Дней в таблице: ______'
          ] }
        ]
      },
      {
        title: 'Цикл растения / Plant Cycle',
        blocks: [
          { type: 'text', text: 'Задание 8 — Что нужно растению? Отметь нужное:' },
          { type: 'list', items: [
            '□ вода',
            '□ свет',
            '□ воздух',
            '□ место для корней',
            '□ конфета',
            '□ игрушка',
            '□ немного тепла',
            '□ телефон'
          ] },
          { type: 'text', text: 'Задание 9 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: [
            'семя   корень   лист   цветок   свет',
            'с_мя     к_рень     л_ст     цвет_к     св_т'
          ] },
          { type: 'text', text: 'Задание 10 — Карточка жизненного цикла. Нарисуй 5 шагов: семя → росток → взрослое растение → цветок → новое семя.' },
          { type: 'text', text: 'Растению нужны __________________, __________________ и __________________.' },
          { type: 'text', text: 'Задание 11 — Объясни родителю. Скажи:' },
          { type: 'list', items: [
            '□ У растения есть корень, стебель, листья и цветок.',
            '□ Растению нужны вода, свет и воздух.',
            '□ Жизненный цикл начинается с семени.'
          ] },
          { type: 'text', text: 'Parent Check: □ прочитала слова  □ прочитала 4 фразы  □ соединила части растения  □ поставила цикл по порядку  □ решила math tasks  □ сделала карточку цикла  □ объяснила родителю.' },
          { type: 'text', text: 'Notebook Mode fallback — если нет принтера, сделай все 11 заданий в тетради по тем же номерам.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает части растения, простой жизненный цикл и потребности растения.',
    mathGoal: 'Вычитание по таблице роста, умножение рядов (4 × 6, 3 × 5), сложение, деление 39 ÷ 3, чтение графика.',
    russianGoal: 'Прочитать слова растение, семя, корень, стебель, лист, цветок, росток, цикл, вода, свет.',
    readingWritingGoal: 'Прочитать 4 фразы, написать 5 слов, подписать жизненный цикл.',
    practicalGoal: 'Сделать карточку жизненного цикла растения.',
    parentTheoryCard: {
      title: 'Как объяснить части растения и жизненный цикл',
      purpose: 'Помочь родителю объяснить растение как живой организм, который имеет части и меняется со временем.',
      keyMessage: 'Растение — живой организм. У него есть части, потребности и жизненный цикл.',
      parentScript: [
        '«Семя может стать ростком.»',
        '«Корень помогает брать воду.»',
        '«Стебель держит растение.»',
        '«Листья помогают растению получать свет.»',
        '«Цикл показывает, как растение меняется.»'
      ],
      practiceSteps: [
        'Нарисуйте растение.',
        'Подпишите части.',
        'Разложите цикл по порядку.',
        'Посмотрите таблицу роста.',
        'Попросите Аниту сказать, что нужно растению.'
      ],
      doNotSay: [
        'Не требовать взрослой ботаники.',
        'Не просить ребёнка срывать растения.',
        'Не предлагать есть листья, семена или ягоды.'
      ],
      paperOnlyNotes: [
        'Можно рисовать выдуманное растение.',
        'Можно наблюдать комнатное растение с родителем.',
        'Не собирать растения на улице без родителя.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится видеть растение как живой организм с частями и циклом.',
    keyMessage: 'Растение растёт не случайно: у него есть части, потребности и этапы роста.',
    howToExplain: 'Скажите: «Жизненный цикл — это путь изменений: семя, росток, растение, цветок, новое семя.»',
    exampleDialogue:
      'Parent: С чего начинается растение?\nChild: С семени.\nParent: Что делает корень?\nChild: Берёт воду.\nParent: Что показывают данные роста?\nChild: Растение стало выше.',
    readingSupport: 'Слова «жизненный», «стебель», «растение» можно читать по частям.',
    russianSpeechGoal: 'Анита говорит части растения, потребности и начало жизненного цикла.',
    mathAnswerKey:
      'math-plant-growth-table: 1) 3 см. 2) 4 см. 3) 5 см. 4) 12 см.\nmath-seed-arrays: 1) 24. 2) 15. 3) 39. 4) 13.\ndata-plant-bar-graph: 1) День 7. 2) День 1, День 3, День 5. 3) 4.',
    spellingAnswerKey: 'семя\nкорень\nлист\nцветок\nсвет',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка русского чтения 10 слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка русского чтения 4 фраз.' },
      { taskId: 'plant-parts-match', answer: '1-D, 2-B, 3-A, 4-E, 5-C.' },
      { taskId: 'life-cycle-order', answer: 'B → A → C → D → E.' },
      { taskId: 'math-plant-growth-table', answer: '3; 4; 5; 12.' },
      { taskId: 'math-seed-arrays', answer: '24; 15; 39; 13.' },
      { taskId: 'data-plant-bar-graph', answer: 'День 7; День 1/3/5; 4.' },
      { taskId: 'science-plant-needs', answer: '1,2,3,4,7 нужны; 5,6,8 не нужны.' },
      { taskId: 'spelling-plant', answer: 'семя, корень, лист, цветок, свет.' },
      { taskId: 'creative-plant-cycle-card', answer: '5-шаговый цикл и потребности растения.' },
      { taskId: 'speech-plant-explain', answer: 'Объяснение частей растения и потребностей.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита назвала части растения.',
      'Анита поставила цикл по порядку.',
      'Анита решила math tasks.',
      'Анита сделала карточку цикла.',
      'Анита объяснила потребности растения.'
    ],
    spellingCheck: 'Проверить семя, корень, лист, цветок, свет.',
    readingCheck: 'Главное — попытка и понимание.',
    practicalCheck: 'Цикл должен иметь 5 шагов.',
    drawingCriteria: 'Есть 5 шагов цикла и подписи.',
    commonMistakes: [
      '«Растению нужна конфета.» Правильно: «Растению нужны вода, свет, воздух и место.»',
      '«Корень — сверху.» Правильно: «Корень обычно в земле.»',
      '«Цикл — это просто список.» Правильно: «Цикл показывает изменения.»'
    ],
    discussionPrompts: [
      'Что делает корень?',
      'Что делают листья?',
      'Как меняется растение?',
      'Что нужно растению?'
    ],
    ifEasy: 'Попросить Аниту добавить ещё 2 дня роста и пересчитать разницу.',
    ifDifficult: 'Оставить только 3 части растения: корень, стебель, лист.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за аккуратный жизненный цикл или ясное объяснение.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не использовать реальные уличные растения без присмотра. Можно использовать рисунок или комнатное растение.',
    safetyNotes: 'Не есть растения, листья, семена, ягоды или грибы. Не срывать неизвестные растения. Наблюдать только с родителем.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'neat-cycle', label: 'Бонус за аккуратный цикл растения', amount: 1 },
      { id: 'clear-plant-needs', label: 'Бонус за ясное объяснение потребностей растения', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'plant-parts-match',
      'life-cycle-order',
      'math-plant-growth-table',
      'math-seed-arrays',
      'data-plant-bar-graph',
      'science-plant-needs',
      'spelling-plant',
      'creative-plant-cycle-card',
      'speech-plant-explain'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-8-day-3'
};

export default day2;
